import {
  RealtimeAgent,
  RealtimeSession,
  type RealtimeItem,
} from '@openai/agents/realtime';

type EventHandler = (event: any) => void | Promise<void>;

export type ItemType = any;

class EventBus {
  private handlers: Record<string, EventHandler[]> = {};

  on(eventName: string, callback: EventHandler) {
    this.handlers[eventName] = this.handlers[eventName] || [];
    this.handlers[eventName].push(callback);
    return callback;
  }

  off(eventName: string, callback?: EventHandler) {
    if (!callback) {
      delete this.handlers[eventName];
      return;
    }

    this.handlers[eventName] = (this.handlers[eventName] || []).filter(
      (handler) => handler !== callback
    );
  }

  protected emit(eventName: string, event: any) {
    for (const handler of this.handlers[eventName] || []) {
      handler(event);
    }
  }

  clearEventHandlers() {
    this.handlers = {};
  }
}

type SessionConfig = {
  instructions?: string;
  modalities?: string[];
  voice?: string;
  input_audio_format?: string;
  output_audio_format?: string;
  input_audio_transcription?: { model: string } | null;
  turn_detection?: Record<string, any> | null;
  tools?: any[];
  tool_choice?: any;
  max_response_output_tokens?: number | 'inf';
};

type ToolConfig = {
  definition: any;
  handler: (args: any) => Promise<any> | any;
};

export class RealtimeClient extends EventBus {
  private apiKey: string;
  private debug: boolean;
  private agent: RealtimeAgent | null = null;
  private session: RealtimeSession | null = null;
  private connected = false;
  private tools: Record<string, ToolConfig> = {};
  private sessionConfig: SessionConfig = {
    instructions: '',
    modalities: ['text', 'audio'],
    voice: 'alloy',
    input_audio_transcription: { model: 'gpt-live-transcribe' },
    turn_detection: { type: 'server_vad' },
    tool_choice: 'auto',
    max_response_output_tokens: 4096,
  };

  conversation = {
    getItems: () => this.getItems(),
    getItem: (id: string) => this.getItems().find((item) => item.id === id) || null,
    clear: () => undefined,
  };

  constructor({
    apiKey,
    debug = false,
  }: {
    apiKey: string;
    dangerouslyAllowAPIKeyInBrowser?: boolean;
    debug?: boolean;
  }) {
    super();
    this.apiKey = apiKey;
    this.debug = debug;
  }

  isConnected() {
    return this.connected;
  }

  async connect() {
    if (this.connected) {
      throw new Error('Already connected, use .disconnect() first');
    }

    this.agent = new RealtimeAgent({
      name: 'Interviewer',
      instructions: this.sessionConfig.instructions || '',
      voice: this.sessionConfig.voice,
    });

    this.session = new RealtimeSession(this.agent, {
      model: process.env.REACT_APP_OPENAI_REALTIME_MODEL || 'gpt-realtime-2',
      transport: 'websocket',
      config: this.toSessionConfig(),
      historyStoreAudio: true,
    });

    this.bindSessionEvents(this.session);

    try {
      await this.session.connect({
        apiKey: this.apiKey,
        model: process.env.REACT_APP_OPENAI_REALTIME_MODEL || 'gpt-realtime-2',
      });

      this.connected = true;
    } catch (error) {
      this.connected = false;
      throw error;
    }
  }

  disconnect() {
    this.session?.close();
    this.connected = false;
    this.session = null;
    this.agent = null;
  }

  reset() {
    this.disconnect();
    this.clearEventHandlers();
    this.tools = {};
  }

  addTool(definition: any, handler: (args: any) => Promise<any> | any) {
    if (!definition?.name) {
      throw new Error('Missing tool name in definition');
    }

    this.tools[definition.name] = { definition, handler };
    return this.tools[definition.name];
  }

  updateSession(config: SessionConfig = {}) {
    this.sessionConfig = {
      ...this.sessionConfig,
      ...Object.fromEntries(
        Object.entries(config).filter(([, value]) => value !== undefined)
      ),
    };

    if (this.canSend()) {
      this.session?.transport.updateSessionConfig(this.toSessionConfig());
    }
  }

  sendUserMessageContent(content: any[] = []) {
    if (!this.canSend() || !content.length) return;

    try {
      this.session?.transport.sendMessage({
        type: 'message',
        role: 'user',
        content: content
          .filter((part) => part.type === 'input_text')
          .map((part) => ({
            type: 'input_text',
            text: part.text,
          })),
      }, {}, { triggerResponse: false });
    } catch (error) {
      this.handleClosedOrSendError(error);
    }
  }

  appendInputAudio(audio: Int16Array | ArrayBuffer) {
    if (!this.canSend() || audio.byteLength === 0) return;

    const buffer =
      audio instanceof Int16Array
        ? audio.buffer.slice(audio.byteOffset, audio.byteOffset + audio.byteLength)
        : audio;

    try {
      this.session?.sendAudio(buffer, { commit: false });
    } catch (error) {
      this.handleClosedOrSendError(error);
    }
  }

  createResponse() {
    if (!this.canSend()) return;

    try {
      this.session?.transport.sendEvent({ type: 'response.create' });
    } catch (error) {
      this.handleClosedOrSendError(error);
    }
  }

  cancelResponse(_id?: string, _sampleCount = 0) {
    this.session?.interrupt();
    return { item: null };
  }

  private bindSessionEvents(session: RealtimeSession) {
    session.on('error', (error) => {
      this.connected = false;
      this.emit('error', error);
    });

    const transport = session.transport as any;
    transport.on?.('disconnected', () => {
      this.connected = false;
    });
    transport.on?.('connection_change', (status: string) => {
      this.connected = status === 'connected';
    });

    session.on('audio', (event) => {
      const audio = new Int16Array(event.data);
      this.emit('conversation.updated', {
        item: this.getAssistantAudioItem(event.responseId, audio),
        delta: { audio },
      });
    });

    session.on('audio_interrupted', () => {
      this.emit('conversation.interrupted', {});
    });

    session.on('transport_event', (event: any) => {
      if (event.type === 'error') {
        this.connected = false;
      }

      this.emit('realtime.event', {
        time: new Date().toISOString(),
        source: 'server',
        event,
      });

      if (event.type === 'input_audio_buffer.speech_stopped') {
        this.emit('input_audio_buffer.speech_stopped', event);
      }

      if (event.type === 'response.audio_transcript.delta') {
        this.emit('response.audio_transcript.delta', {
          item_id: event.item_id,
          delta: event.delta,
        });
      }

      if (event.type === 'response.done') {
        this.emit('response.done', event);
      }
    });

    session.on('history_updated', () => {
      this.emit('conversation.updated', {
        item: this.getItems().at(-1),
        delta: null,
      });
    });

    session.on('history_added', (item) => {
      this.emit('conversation.updated', {
        item: this.toLegacyItem(item),
        delta: null,
      });
    });
  }

  private getItems() {
    return (this.session?.history || []).map((item) => this.toLegacyItem(item));
  }

  private toLegacyItem(item: RealtimeItem): ItemType {
    const content = 'content' in item ? item.content || [] : [];
    const transcript = content
      .map((part: any) => part.transcript || part.text || '')
      .join('');
    const text = content.map((part: any) => part.text || '').join('');

    return {
      ...item,
      id: item.itemId,
      formatted: {
        audio: new Int16Array(0),
        text,
        transcript,
      },
    };
  }

  private getAssistantAudioItem(responseId: string, audio: Int16Array) {
    return {
      id: responseId,
      itemId: responseId,
      type: 'message',
      role: 'assistant',
      status: 'in_progress',
      content: [],
      formatted: {
        audio,
        text: '',
        transcript: '',
      },
    };
  }

  private toSessionConfig() {
    const outputModalities = this.sessionConfig.modalities?.includes('audio')
      ? ['audio']
      : ['text'];

    return {
      instructions: this.sessionConfig.instructions || '',
      outputModalities,
      audio: {
        input: {
          format: { type: 'audio/pcm', rate: 24000 },
          transcription: this.sessionConfig.input_audio_transcription || undefined,
          turnDetection: this.sessionConfig.turn_detection || undefined,
        },
        output: outputModalities.includes('audio')
          ? {
              format: { type: 'audio/pcm', rate: 24000 },
              voice: this.sessionConfig.voice || 'alloy',
            }
          : undefined,
      },
      toolChoice: this.sessionConfig.tool_choice || 'auto',
      tools: Object.values(this.tools).map(({ definition }) => ({
        type: 'function',
        name: definition.name,
        description: definition.description || '',
        parameters: definition.parameters || { type: 'object', properties: {} },
        strict: false,
      })),
      maxOutputTokens: this.sessionConfig.max_response_output_tokens,
    } as any;
  }

  private canSend() {
    const status = (this.session?.transport as any)?.status;
    return !!this.session && this.connected && (!status || status === 'connected');
  }

  private handleClosedOrSendError(error: any) {
    const message = error?.message || String(error);
    if (message.includes('CLOSING') || message.includes('CLOSED') || message.includes('not connected')) {
      this.connected = false;
      return;
    }

    this.emit('error', {
      type: 'error',
      error,
    });
  }
}
